import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Button, Pagination, Table, Badge } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiEdit, FiSearch, FiX, FiEye, FiPlus, FiTrash2, FiDollarSign } from "react-icons/fi";
import Swal from "sweetalert2";

export default function InHousePurchaseIndex() {
    const { t } = useContext(TranslatorContext);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Get today's date
    const today = new Date().toISOString().split("T")[0];

    // Filter states
    const [tempFilters, setTempFilters] = useState({
        vendor_id: searchParams.get('vendor_id') || "",
        search: searchParams.get('search') || "",
        from_date: searchParams.get('from_date') || today,
        to_date: searchParams.get('to_date') || today,
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [activeFilters, setActiveFilters] = useState({
        vendor_id: searchParams.get('vendor_id') || "",
        search: searchParams.get('search') || "",
        from_date: searchParams.get('from_date') || today,
        to_date: searchParams.get('to_date') || today,
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [applyingFilter, setApplyingFilter] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    // Fetch vendors
    const fetchVendors = useCallback(async () => {
        try {
            const token = localStorage.getItem("authToken");
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { per_page: 100, status: 'active' }
            });
            setVendors(res.data.data || []);
        } catch (err) {
            console.error("Error fetching vendors:", err);
        }
    }, []);

    // Fetch purchases with filters
    const fetchPurchases = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            const params = new URLSearchParams();
            if (activeFilters.vendor_id) params.append('vendor_id', activeFilters.vendor_id);
            if (activeFilters.search) params.append('search', activeFilters.search);
            if (activeFilters.from_date) params.append('from_date', activeFilters.from_date);
            if (activeFilters.to_date) params.append('to_date', activeFilters.to_date);
            if (activeFilters.per_page) params.append('per_page', activeFilters.per_page);
            if (activeFilters.page) params.append('page', activeFilters.page);

            const url = `${process.env.REACT_APP_API_URL}/inhouse/purchases${params.toString() ? `?${params.toString()}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const purchasesData = res.data.data || [];
            setPurchases(purchasesData);
            setPagination({
                current_page: res.data.meta?.current_page || 1,
                last_page: res.data.meta?.last_page || 1,
                per_page: res.data.meta?.per_page || 10,
                total: res.data.meta?.total || 0,
                from: res.data.meta?.from || 0,
                to: res.data.meta?.to || 0
            });
        } catch (err) {
            console.error("Error fetching purchases:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to fetch purchases'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    }, [activeFilters, t]);

    useEffect(() => {
        fetchVendors();
        fetchPurchases();
    }, [fetchVendors, fetchPurchases]);

    // Handle filter change
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({ ...prev, [name]: value }));
    };

    // Apply filters
    const applyFilters = () => {
        setActiveFilters({
            ...tempFilters,
            page: 1
        });

        const params = new URLSearchParams();
        if (tempFilters.vendor_id) params.append('vendor_id', tempFilters.vendor_id);
        if (tempFilters.search) params.append('search', tempFilters.search);
        if (tempFilters.from_date) params.append('from_date', tempFilters.from_date);
        if (tempFilters.to_date) params.append('to_date', tempFilters.to_date);
        if (tempFilters.per_page && tempFilters.per_page !== 10) params.append('per_page', tempFilters.per_page);
        setSearchParams(params);

        setApplyingFilter(true);
        setTimeout(() => setApplyingFilter(false), 500);
    };

    // Reset filters
    const resetFilters = () => {
        const emptyFilters = {
            vendor_id: "",
            search: "",
            from_date: today,
            to_date: today,
            per_page: 10,
            page: 1
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setSearchParams({});
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setActiveFilters(prev => ({ ...prev, page: page }));
        }
    };

    // Handle delete purchase
    const handleDelete = async (id, purchaseNumber) => {
        const result = await Swal.fire({
            title: t('Are you sure?'),
            text: `${t('Delete purchase')}: ${purchaseNumber}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        });

        if (result.isConfirmed) {
            setDeletingId(id);
            try {
                const token = localStorage.getItem("authToken");
                await axios.delete(`${process.env.REACT_APP_API_URL}/inhouse/purchases/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Purchase deleted successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });
                fetchPurchases();
            } catch (err) {
                console.error("Error deleting purchase:", err);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: err.response?.data?.message || t('Failed to delete purchase'),
                    showConfirmButton: false,
                    timer: 3000
                });
            } finally {
                setDeletingId(null);
            }
        }
    };

    // Get payment status badge
    const getPaymentStatusBadge = (status) => {
        switch (status) {
            case 'paid':
                return <Badge bg="success">{t('Paid')}</Badge>;
            case 'partial':
                return <Badge bg="warning">{t('Partial')}</Badge>;
            case 'unpaid':
                return <Badge bg="danger">{t('Unpaid')}</Badge>;
            default:
                return <Badge bg="secondary">{t('Unknown')}</Badge>;
        }
    };

    if (loading && purchases.length === 0) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </div>
                    <p className="mt-3">{t("Loading purchases...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('In-House Purchases')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="/dashboard" className="mc-breadcrumb-link">{t('Home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('In-House Purchases')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('Purchase List')}</h5>
                            {hasPermission("InHouse Purchase create") && (
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => navigate("/inhouse/purchase-create")}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <FiPlus size={18} />
                                    {t("Create New Purchase")}
                                </Button>
                            )}
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom bg-light">
                            <Row className="g-3">
                                <Col xs={12} sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Purchase Number")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="search"
                                            value={tempFilters.search}
                                            onChange={handleFilterChange}
                                            placeholder={t("Search by PO number...")}
                                            size="sm"
                                            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Vendor")}</Form.Label>
                                        <Form.Select
                                            name="vendor_id"
                                            value={tempFilters.vendor_id}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        >
                                            <option value="">{t('All Vendors')}</option>
                                            {vendors.map(vendor => (
                                                <option key={vendor.id} value={vendor.id}>
                                                    {vendor.name} {vendor.company_name ? `(${vendor.company_name})` : ''}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("From Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="from_date"
                                            value={tempFilters.from_date}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("To Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="to_date"
                                            value={tempFilters.to_date}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={2}>
                                    <div className="d-flex gap-2 justify-content-end mt-2">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={resetFilters}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiX size={14} /> {t("Reset")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={applyFilters}
                                            disabled={applyingFilter}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            {applyingFilter ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm" role="status" />
                                                    <span>{t("Applying...")}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSearch size={14} /> {t("Search")}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Results Info */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white flex-wrap gap-2">
                            <div className="text-muted small">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("entries")}
                            </div>
                            {loading && (
                                <div className="text-muted small">
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </div>
                            )}
                        </div>

                        {/* Responsive Table */}
                        <div className="table-responsive">
                            <Table responsive hover className="table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '15%' }}>{t('PO Number')}</th>
                                        <th style={{ width: '15%' }}>{t('Purchase Date')}</th>
                                        <th style={{ width: '20%' }}>{t('Vendor')}</th>
                                        <th style={{ width: '20%' }}>{t('Summary')}</th>
                                        <th className="text-end" style={{ width: '12%' }}>{t('Total')}</th>
                                        <th className="text-center" style={{ width: '10%' }}>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchases.length > 0 ? (
                                        purchases.map((purchase, index) => (
                                            <tr key={purchase.id}>
                                                <td className="text-center">
                                                    {purchase.id ?? '-'}
                                                </td>
                                                <td>
                                                    <strong>{purchase.purchase_number}</strong>
                                                </td>
                                                <td>{purchase.purchase_date}</td>
                                                <td>
                                                    <div>
                                                        <strong>{purchase.vendor?.company_name}</strong>
                                                        {purchase.vendor?.company_name && (
                                                            <div className="small text-muted">{purchase.vendor.name}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        {/* Products Summary */}
                                                        <div className="d-flex flex-column gap-1">

                                                            {purchase.items_summary?.map((item, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className="border rounded p-2 bg-light"
                                                                    style={{ fontSize: "12px" }}
                                                                >
                                                                    <div className="fw-bold text-dark">
                                                                        {item.product_name}
                                                                    </div>

                                                                    <div className="text-muted">
                                                                        {item.purchase_qty} {item.purchase_unit}(s)
                                                                        × {item.unit_quantity} {item.unit_type}
                                                                        {item.extra_loose_qty > 0 && (
                                                                            <> + {item.extra_loose_qty} loose</>
                                                                        )}
                                                                    </div>


                                                                </div>
                                                            ))}

                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {parseFloat(purchase.grand_total || 0)}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <Button
                                                            variant="info"
                                                            size="sm"
                                                            onClick={() => navigate(`/inhouse/purchase-view/${purchase.id}`)}
                                                            title={t("View Details")}
                                                            className="d-flex align-items-center gap-1"
                                                        >
                                                            <FiEye size={14} /> {t("View")}
                                                        </Button>
                                                        {hasPermission("InHouse Purchase edit") && (
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                onClick={() => navigate(`/inhouse/purchase-edit/${purchase.id}`)}
                                                                title={t("Edit Purchase")}
                                                                className="d-flex align-items-center gap-1"
                                                            >
                                                                <FiEdit size={14} /> {t("Edit")}
                                                            </Button>
                                                        )}
                                                        {hasPermission("InHouse Purchase delete") && (
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(purchase.id, purchase.purchase_number)}
                                                                title={t("Delete")}
                                                                disabled={deletingId === purchase.id}
                                                                className="d-flex align-items-center gap-1"
                                                            >
                                                                {deletingId === purchase.id ? (
                                                                    <div className="spinner-border spinner-border-sm" role="status" />
                                                                ) : (
                                                                    <FiTrash2 size={14} />
                                                                )}
                                                                {t("Delete")}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5">
                                                <div className="empty-state">
                                                    <FiDollarSign size={48} className="text-muted mb-3" />
                                                    <p className="mb-2">{t("No purchases found")}</p>
                                                    {Object.values(activeFilters).some(v => v && v !== 1 && v !== 10) && (
                                                        <Button
                                                            variant="link"
                                                            onClick={resetFilters}
                                                            className="text-primary"
                                                        >
                                                            {t("Clear filters")}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                </div>
                                <Pagination className="mb-0 flex-wrap justify-content-center">
                                    <Pagination.First
                                        onClick={() => handlePageChange(1)}
                                        disabled={pagination.current_page === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    />

                                    {(() => {
                                        const pages = [];
                                        const maxPages = Math.min(5, pagination.last_page);
                                        let startPage = Math.max(1, pagination.current_page - 2);
                                        let endPage = Math.min(pagination.last_page, startPage + maxPages - 1);

                                        if (endPage - startPage + 1 < maxPages) {
                                            startPage = Math.max(1, endPage - maxPages + 1);
                                        }

                                        for (let i = startPage; i <= endPage; i++) {
                                            pages.push(
                                                <Pagination.Item
                                                    key={i}
                                                    active={i === pagination.current_page}
                                                    onClick={() => handlePageChange(i)}
                                                >
                                                    {i}
                                                </Pagination.Item>
                                            );
                                        }
                                        return pages;
                                    })()}

                                    <Pagination.Next
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                    <Pagination.Last
                                        onClick={() => handlePageChange(pagination.last_page)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}